import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import { debounce } from "lodash";

import { useDispatch } from "react-redux";
import { getCaseById } from "../../features/slices/Case";
import { fetchCaseReport } from "../../features/slices/Report";
import Title from "../../components/Title";
import { useNotification } from "../../context/NotificationContext";
import { useLoader } from "../../context/LoaderContext";
import { JsonEditor } from "json-edit-react";
import ManifestViewer from "../../components/ManifestViewer";
import { AppDispatch } from "../../store";
import { Case } from "../../interfaces/Case";
import ReportBrowser from "./components/ReportBrowser";

const resultColor = (
  value?: string
): "success" | "error" | "warning" | "info" | "default" => {
  const v = (value || "").toLowerCase();
  if (["success", "passed", "pass"].some((s) => v.includes(s))) return "success";
  if (["fail", "error", "abort"].some((s) => v.includes(s))) return "error";
  if (["queue", "progress", "prepare", "reserved"].some((s) => v.includes(s)))
    return "info";
  if (["init", "pending", "skip"].some((s) => v.includes(s))) return "warning";
  return "default";
};

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
    <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const CaseView = () => {
  const { caseId } = useParams();
  const [singleCase, setCase] = useState<Case>({
    title: "",
    tc_result: "",
    sys_result: "",
    file_name: "",
    original_path: "",
    meta_data: {},
    manifest: {},
  } as Case);
  const { notify } = useNotification();
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const fetchSuite = useCallback(
    debounce(async () => {
      showLoader();
      try {
        const result = await dispatch(getCaseById(Number(caseId)) as any);
        hideLoader();
        if (result.payload) setCase(result.payload);
      } catch (error) {
        hideLoader();
        console.error("Failed to fetch case", error);
        notify("Failed to fetch case", "error");
      }
    }, 300),
    [dispatch, caseId]
  );

  useEffect(() => {
    fetchSuite();
  }, [dispatch, caseId, fetchSuite]);

  const handleDownload = async () => {
    try {
      const result = await dispatch(fetchCaseReport(Number(caseId))).unwrap();
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(result.data)], { type: "application/zip" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `case_${caseId}.zip`);
      document.body.appendChild(link);
      link.click();
      notify("Downloaded report.", "success");
    } catch (error) {
      console.error("Error downloading report:", error);
      notify("Failed to download report.", "error");
    }
  };

  const jsonEditorProps = {
    minWidth: "100%",
    collapse: 1,
    restrictAdd: true,
    restrictDelete: true,
    restrictEdit: true,
  } as const;

  return (
    <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto", pb: 6 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Title>View Case {caseId}</Title>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={!singleCase.report_exists}
          >
            Download result
          </Button>
        </Stack>
      </Stack>

      <SectionCard title="Summary">
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Title
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {singleCase.title || "—"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                TC Result
              </Typography>
              <Chip
                size="small"
                label={singleCase.tc_result || "—"}
                color={resultColor(singleCase.tc_result)}
                sx={{ mt: 0.5, fontWeight: 700, textTransform: "uppercase" }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Sys Result
              </Typography>
              <Chip
                size="small"
                label={singleCase.sys_result || "—"}
                color={resultColor(singleCase.sys_result)}
                sx={{ mt: 0.5, fontWeight: 700, textTransform: "uppercase" }}
              />
            </Box>
          </Stack>
        </Stack>
      </SectionCard>

      <SectionCard title="Resource manifest">
        <ManifestViewer data={singleCase.manifest ?? {}} />
      </SectionCard>
      <SectionCard title="Tool Data">
        <JsonEditor {...jsonEditorProps} data={singleCase.tool_data ?? {}} />
      </SectionCard>
      <SectionCard title="Meta Data">
        <JsonEditor {...jsonEditorProps} data={singleCase.meta_data ?? {}} />
      </SectionCard>

      <ReportBrowser
        caseId={Number(caseId)}
        reportExists={!!singleCase.report_exists}
      />
    </Box>
  );
};

export default CaseView;
